import { useEffect, useRef, useState } from "react";
import type { McpTokenResult, McpTokenStatusView } from "../hooks/useAdminClaws";
import type { AdminOpenClaw } from "../types/openclaw";
import { formatDateTime } from "../utils/formatDateTime";
import styles from "./McpGatewayModal.module.css";

type Props = {
  open: boolean;
  claw: AdminOpenClaw | null;
  fetchStatus: (tenantId: string) => Promise<McpTokenStatusView | null>;
  onRotate: (tenantId: string) => Promise<McpTokenResult>;
  onPush: (tenantId: string) => Promise<McpTokenResult>;
  onFlash: (message: string) => void;
  onClose: () => void;
};

// Admin per-tenant MCP gateway wiring. Shows current status (configured /
// token prefix / last updated) and offers two actions:
//   - Rotate & wire: mint a fresh token, ensure DNS, push to the VM.
//   - Re-push existing: re-deliver the stored token (DNS + push, no rotate).
// Both restart the gateway, so the customer sees a brief disruption.
export const McpGatewayModal = ({
  open,
  claw,
  fetchStatus,
  onRotate,
  onPush,
  onFlash,
  onClose,
}: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusView, setStatusView] = useState<McpTokenStatusView | null>(null);
  const [busy, setBusy] = useState<null | "rotate" | "push">(null);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setStatusView(null);
      setServerError(null);
      setBusy(null);
      setLoading(true);
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !claw) return;
    let cancelled = false;
    setLoading(true);
    void fetchStatus(claw.tenantId).then((s) => {
      if (cancelled) return;
      setStatusView(s);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, claw, fetchStatus]);

  if (!open || !claw) return null;

  const run = async (kind: "rotate" | "push"): Promise<void> => {
    setBusy(kind);
    setServerError(null);
    const result = await (kind === "rotate" ? onRotate : onPush)(claw.tenantId);
    setBusy(null);
    if (result.ok) {
      onFlash(
        kind === "rotate"
          ? `Rotated + wired ${claw.name} to the MCP gateway.`
          : `Re-pushed ${claw.name} to the MCP gateway.`,
      );
      onClose();
      return;
    }
    setServerError(result.message);
  };

  const configured = statusView?.configured ?? false;
  // Local dev has no runtime VM-wiring path (the backend short-circuits), so
  // disable the actions outright rather than let them appear to succeed.
  const localDev = import.meta.env.DEV;
  const disabled = busy !== null || loading || localDev;

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={(evt) => {
        evt.preventDefault();
        if (busy === null) onClose();
      }}
    >
      <div className={styles.form}>
        <header className={styles.header}>
          <h2 className={styles.title}>MCP Gateway</h2>
          <p className={styles.subtitle}>
            Wire <span className={styles.nameToken}>{claw.name}</span> to the MCP gateway: mint a
            token, fix DNS, patch openclaw.json, and restart the gateway.
          </p>
        </header>

        <dl className={styles.status}>
          <div className={styles.statusRow}>
            <dt className={styles.label}>Status</dt>
            <dd>
              {loading ? (
                <span className={styles.muted}>Loading…</span>
              ) : configured ? (
                <span className={`${styles.badge} ${styles.badgeOk}`}>Token set</span>
              ) : (
                <span className={`${styles.badge} ${styles.badgeOff}`}>No token</span>
              )}
            </dd>
          </div>
          <div className={styles.statusRow}>
            <dt className={styles.label}>Token</dt>
            <dd className={styles.mono}>{statusView?.prefix ? `${statusView.prefix}…` : "—"}</dd>
          </div>
          <div className={styles.statusRow}>
            <dt className={styles.label}>Updated</dt>
            <dd className={styles.muted}>
              {statusView?.updatedAt ? formatDateTime(statusView.updatedAt) : "—"}
            </dd>
          </div>
        </dl>

        {import.meta.env.DEV && (
          <p className={styles.devNote}>
            Local dev: instances are wired at provision time. Rotate / Re-push are disabled here — both rely on AWS SSM, which only prod uses.
          </p>
        )}

        <div className={styles.warn}>
          Both actions restart the gateway, there will be a brief disruption for the customer.
        </div>

        {serverError && <div className={styles.errorBanner}>{serverError}</div>}

        <footer className={styles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={busy !== null}>
            Cancel
          </button>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => {
                void run("push");
              }}
              disabled={disabled || !configured}
              title={localDev ? "Disabled in local dev — instances are wired at provision time." : configured ? "Re-deliver the existing token to the VM (DNS + push, no new token)." : "No token yet — rotate to create one."}
            >
              {busy === "push" ? "Pushing…" : "Re-push"}
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => {
                void run("rotate");
              }}
              disabled={disabled}
              title={localDev ? "Disabled in local dev — instances are wired at provision time." : "Mint a new token, fix DNS, patch openclaw.json, and restart the gateway."}
            >
              {busy === "rotate" ? "Rotating…" : "Rotate & wire"}
            </button>
          </div>
        </footer>
      </div>
    </dialog>
  );
};
